import {
  addWeeks,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  isWithinInterval,
  parseISO,
  startOfWeek,
  subWeeks,
} from 'date-fns';

/**
 * Format seconds to MM:SS or HH:MM:SS format
 */
export const formatPace = (secondsPerKm: number): string => {
  const minutes = Math.floor(secondsPerKm / 60);
  const seconds = Math.floor(secondsPerKm % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Format duration in seconds to readable format
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Format distance in kilometers
 */
export const formatDistance = (km: number): string => {
  return km % 1 === 0 ? `${km} km` : `${km.toFixed(1)} km`;
};

/**
 * Get the week dates for a given date
 */
export const getWeekDates = (date: Date): Date[] => {
  const start = startOfWeek(date, { weekStartsOn: 1 }); // Monday
  const end = endOfWeek(date, { weekStartsOn: 1 }); // Sunday
  return eachDayOfInterval({ start, end });
};

/**
 * Get week start and end dates
 */
export const getWeekRange = (date: Date): { start: Date; end: Date } => {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const end = endOfWeek(date, { weekStartsOn: 1 });
  return { start, end };
};

/**
 * Check if a date is within the current week
 */
export const isDateInWeek = (date: Date, weekDate: Date): boolean => {
  const { start, end } = getWeekRange(weekDate);
  return isWithinInterval(date, { start, end });
};

/**
 * Format date for display
 */
export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'EEE ddMMM');
};

/**
 * Format date for calendar display
 */
export const formatCalendarDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'dd');
};

/**
 * Format day of week
 */
export const formatDayOfWeek = (date: Date | string): string => {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'EEE');
};

/**
 * Format week range for display
 */
export const formatWeekRange = (date: Date): string => {
  const { start, end } = getWeekRange(date);
  return `${format(start, 'd')} - ${format(end, 'd MMM')}`;
};

/**
 * Navigate to next week
 */
export const getNextWeek = (date: Date): Date => {
  return addWeeks(date, 1);
};

/**
 * Navigate to previous week
 */
export const getPreviousWeek = (date: Date): Date => {
  return subWeeks(date, 1);
};

/**
 * Get ISO date string from Date object
 */
export const toISODateString = (date: Date): string => {
  return format(date, 'yyyy-MM-dd');
};

/**
 * Parse ISO date string to Date object
 */
export const fromISODateString = (dateString: string): Date => {
  return parseISO(dateString);
};

/**
 * Check if two dates are the same day
 */
export { isSameDay };
